import React from 'react'
import { Link } from 'react-router-dom'
import { usePartnerAuth } from '../../context/PartnerAuthContext'
import { usePartnerTheme } from '../../hooks/usePartnerTheme'
import { usePartnerDashboardData } from '../../hooks/usePartnerDashboardData'
import StatCard from '../../components/partner/StatCard'
import CouponActiveCard from '../../components/partner/CouponActiveCard'
import OrdersMiniTable from '../../components/partner/OrdersMiniTable'
import PartnerIdentityCard from '../../components/partner/PartnerIdentityCard'
import PartnerContactCard from '../../components/partner/PartnerContactCard'

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF' }).format(amount)

// ── Skeleton placeholder ────────────────────────────────────────

const SkeletonBlock: React.FC<{ height?: string; width?: string }> = ({ height = '1rem', width = '100%' }) => (
  <div
    style={{
      height,
      width,
      background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite',
      borderRadius: '0.5rem',
    }}
  />
)

const SkeletonStatCards: React.FC = () => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
    {[1, 2, 3, 4].map((i) => (
      <div key={i} style={{ background: '#f8fafc', borderRadius: '0.75rem', padding: '1.25rem 1.5rem' }}>
        <SkeletonBlock height="0.85rem" width="60%" />
        <div style={{ marginTop: '0.5rem' }}><SkeletonBlock height="1.75rem" width="40%" /></div>
      </div>
    ))}
  </div>
)

/**
 * PartnerOverview — Vue d'ensemble du Partner Portal (route /partner/dashboard)
 *
 * Sections :
 *   1) Identité + Coordonnées (top)
 *   2) Coupon actif
 *   3) KPIs commandes du mois
 *   4) Dernières commandes (mini-table)
 */
const PartnerOverview: React.FC = () => {
  const { partner, partnerProfile, isAdminPartner } = usePartnerAuth()
  const theme = usePartnerTheme()
  const { stats, activeCoupon, recentOrders, loading, error, retry } = usePartnerDashboardData()

  if (!partner || !partnerProfile) return null

  return (
    <div>
      {/* Shimmer animation keyframes */}
      <style>{`
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @media (max-width: 768px) {
          .overview-top-grid { grid-template-columns: 1fr !important; }
          .overview-stats-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 480px) {
          .overview-stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Erreur banner ── */}
      {error && (
        <div
          data-testid="error-banner"
          style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#991b1b',
            fontSize: '0.9rem',
          }}
        >
          <span>⚠️ {error}</span>
          <button
            onClick={retry}
            data-testid="retry-button"
            style={{
              padding: '0.35rem 0.7rem',
              background: '#991b1b',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.82rem',
              fontWeight: '500',
            }}
          >
            Réessayer
          </button>
        </div>
      )}

      {/* ── Bouton réservation ── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Link
          to="/partner/book"
          data-testid="partner-book-button"
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            background: theme.primary,
            color: 'white',
            borderRadius: '0.5rem',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          🎾 Nouvelle réservation partenaire
        </Link>
      </div>

      {/* ── Section 1 : Identité + Coordonnées ── */}
      <div
        className="overview-top-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <PartnerIdentityCard
          partner={partner}
          profile={partnerProfile}
          isAdmin={isAdminPartner}
          accentColor={theme.primary}
        />
        <PartnerContactCard partner={partner} accentColor={theme.primary} />
      </div>

      {/* ── Section 2 : Coupon actif ── */}
      <div style={{ marginBottom: '1.5rem' }}>
        {loading ? (
          <div style={{ background: '#f8fafc', borderRadius: '0.75rem', padding: '1.5rem' }}>
            <SkeletonBlock height="1.1rem" width="30%" />
            <div style={{ marginTop: '1rem' }}><SkeletonBlock height="2rem" width="50%" /></div>
          </div>
        ) : (
          <CouponActiveCard coupon={activeCoupon} isAdmin={false} accentColor={theme.primary} />
        )}
      </div>

      {/* ── Section 3 : KPIs du mois ── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
          📊 Commandes du mois
        </h3>
        {loading ? (
          <SkeletonStatCards />
        ) : stats ? (
          <div
            className="overview-stats-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
            }}
          >
            <StatCard
              label="Commandes"
              value={stats.orders_count}
              gradient="linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)"
              icon="📦"
            />
            <StatCard
              label="Commission totale"
              value={formatCurrency(stats.commission_sum)}
              gradient="linear-gradient(135deg, #10b981 0%, #047857 100%)"
              icon="💰"
            />
            <StatCard
              label="Remises membres"
              value={formatCurrency(stats.discount_sum)}
              gradient="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)"
              icon="🏷️"
            />
            <StatCard
              label="En cours"
              value={stats.in_progress_count}
              gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              icon="⏳"
              subtitle="en attente / en cours"
            />
          </div>
        ) : null}
      </div>

      {/* ── Section 4 : Dernières commandes ── */}
      <div
        style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '0.75rem',
          padding: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1e293b' }}>
            📋 Dernières commandes
          </h3>
          <Link
            to="/partner/orders"
            style={{ color: theme.primary, textDecoration: 'none', fontWeight: '500', fontSize: '0.9rem' }}
          >
            Voir toutes →
          </Link>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[1, 2, 3].map((i) => (
              <SkeletonBlock key={i} height="2.5rem" />
            ))}
          </div>
        ) : (
          <OrdersMiniTable orders={recentOrders} accentColor={theme.primary} />
        )}
      </div>

    </div>
  )
}

export default PartnerOverview
